import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCancelConsultMetadata,
  getConsultDetailsById,
  getConsultsMetaData,
  getIntakeFormForConsult,
  saveCancelConsult,
  saveConsultsForm,
  saveOrUpdateConsultsForm,
} from '../services/consultService';
import type {
  CancelConsultMetadataResponse,
  ConsultForm,
  ConsultResponse,
  ConsultsMetaData,
} from '../services/consultService';
export type { ConsultForm, ConsultResponse } from '../services/consultService';
import type { DynamicForm } from '@dynamicForm/FieldConfig';
import { type CancelReasonFacilityParams } from '../../pages/dashboard/CancelConsultConfirmationPopup';

export const useConsultsMetaData = (facilityUserId: number) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ConsultsMetaData>({
      queryKey: ['ConsultsMetaData', facilityUserId],
      queryFn: async () => {
        const data = await getConsultsMetaData(facilityUserId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    ConsultsMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetIntakeFormForConsult = (
  userId: string,
  facilityId: number,
  consultId?: number
) => {
  const { data, isLoading, isError, error, refetch } = useQuery<DynamicForm>({
    queryKey: ['DynamicIntakeForm', userId, facilityId, consultId],

    queryFn: async () => {
      if (consultId !== undefined) {
        return getIntakeFormForConsult(userId, facilityId, consultId);
      }
      return getIntakeFormForConsult(userId, facilityId); // no consultId
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!facilityId,
  });

  return {
    DynamicIntakeForm: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveConsult = (
  onSuccess: (response: ConsultResponse) => void
) => {
  return useMutation<ConsultResponse, Error, ConsultForm & { userId: string }>({
    mutationFn: async ({ userId, ...payload }) => {
      const data = await saveConsultsForm(userId, payload);
      return data;
    },

    onSuccess: (response) => {
      onSuccess(response);
    },
    onError: (error) => {
      console.error('Error saving consult:', error);
    },
  });
};

export const useGetConsultDetailsById = (userId: string, consultId: number) => {
  const { data, isLoading, isPending, isError, error, refetch } =
    useQuery<ConsultForm>({
      queryKey: ['ConsultDetailsById', userId, consultId],
      queryFn: async () => {
        const data = await getConsultDetailsById(userId, consultId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!consultId,
    });

  return {
    ConsultDetailsById: data,
    isLoading,
    isPending,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateConsult = (
  onSuccess: (response: ConsultResponse) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ConsultResponse,
    Error,
    ConsultForm & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateConsultsForm(userId, action, payload);
      return data;
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['ConsultsListResponse'],
      });
      onSuccess(response);
    },
    onError: (error) => {
      console.error('Error saving or updating consult:', error);
    },
  });
};

export const useSaveCancelConsult = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    CancelReasonFacilityParams,
    Error,
    CancelReasonFacilityParams & { userId: string; consultId: number }
  >({
    mutationFn: async ({ userId, consultId, ...payload }) => {
      const data = await saveCancelConsult(userId, consultId, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ConsultsListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving consult cancel reason:', error);
    },
  });
};

export const useCancelConsultMetadata = () => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<CancelConsultMetadataResponse>({
      queryKey: ['CancelConsultMetadata'],
      queryFn: async () => {
        const data = await getCancelConsultMetadata();
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: true,
    });

  return {
    CancelConsultMetadata: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
